import React from 'react';

import {useSelector} from 'react-redux';

import {RTKGenericAttachedItemsSidebar} from 'app/components/general/GenericAttachedItemsSidebar/RTKGenericAttachedItemsSidebar';
import NoResultsMessage from 'app/components/general/NoResultsMessage';
import {CurrentUserSelector} from 'app/current-user/api';
import {getCurrentUserPerms} from 'app/current-user/utils';
import {useInfiniteListFetcherRTK} from 'app/general/datafetchers/hooks/useInfiniteListFetcherRTK';
import {sweetAPI} from 'app/store/sweet-api';
import {
  type ModelChangeMessageType,
  runWhenAllRelatedMatch,
} from 'app/subscriptions';
import {useAddQuizzesToTeam, useRemoveTeamItem, useTeams} from 'app/teams/api';
import {documentTeamVia} from 'app/teams/selectors';
import {convertLocationParamsToFetchParams} from 'app/teams/utils';
import {TEAMS_CREATE_PAGE_URL} from 'app/urls';
import {SORT_BY_TYPES} from 'app/utils/constants';
import {getContentType} from 'app/utils/content-type';
import {gt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';
import {TEAMTAG} from 'app/utils/rtk-tags';

import type {UseListQueryType} from 'app/store/sweet-api/api-query';
import type {TeamsFetchParamsType} from 'app/teams/types';
import type {
  TeamMembershipSerializedType,
  TeamSerializedType,
} from 'sweet-types';

type Props = {
  id: string;
  url: string;
  canEdit: boolean;
  contentType: 'quiz';
  closeUrl: string;
  team_memberships: Array<TeamMembershipSerializedType>;
};

export const RTKDocumentTeamsSidebar = ({
  id,
  url,
  closeUrl,
  contentType,
  canEdit,
  team_memberships,
}: Props) => {
  const {results: attachedTeams} = useInfiniteListFetcherRTK({
    fetchParams: {
      document_url: url,
      ordering: SORT_BY_TYPES.CREATION_DATE,
    },
    useDataHook: useTeams,
    paramsConverter: convertLocationParamsToFetchParams,
  });
  const [addDocToTeam] = useAddQuizzesToTeam();
  const [removeTeamItem] = useRemoveTeamItem();
  const {isManager, isSuperManager} = getCurrentUserPerms(
    useSelector((state) => CurrentUserSelector(state)?.data)
  );

  const actions = useAsyncActions({
    invalidateTags: sweetAPI.util.invalidateTags,
  });

  return (
    <RTKGenericAttachedItemsSidebar<
      TeamSerializedType,
      TeamsFetchParamsType,
      TeamSerializedType,
      UseListQueryType<TeamsFetchParamsType, TeamSerializedType>
    >
      title={gt('Teams')}
      searchPlaceholder={gt('Search teams...')}
      itemNameProcessor={(team) => team.name}
      handleModelChange={(message: ModelChangeMessageType) => {
        runWhenAllRelatedMatch(
          message,
          'team',
          {
            content_type: contentType,
            id,
          },
          () => {
            // @ts-expect-error we inject tags on the API after it's created so TS doesn't know bout them
            actions.invalidateTags([{type: TEAMTAG, id: 'list'}]);
          }
        );
      }}
      closeUrl={closeUrl}
      attachedItems={attachedTeams}
      canCreate={isSuperManager}
      createLabel={gt('Create team')}
      createURL={TEAMS_CREATE_PAGE_URL}
      onAttach={(team) => addDocToTeam({team, urls: [url]}).unwrap()}
      onRemove={(team) =>
        removeTeamItem({
          teamURL: team.url,
          item: {url, team_memberships},
        })
      }
      showFilterField={canEdit}
      emptyResultsComponents={
        <NoResultsMessage
          onSidebar={true}
          icon="fa-users"
          head={gt('No Teams')}
        >
          {isManager && canEdit
            ? gt(
                `You can add this ${
                  getContentType(contentType).name
                } to existing teams, remove it from a team or create a new team using the button below`
              )
            : null}
        </NoResultsMessage>
      }
      itemComponentProps={(team, attached) => {
        const via = documentTeamVia(team_memberships, team);
        return {
          disableAndShowTooltipText:
            attached && via.via == 'process'
              ? gt(
                  'The document was added to this team via a process %s',
                  via.name
                )
              : attached && via.via == 'folder'
              ? gt(
                  'The document was added to this team via a folder %s',
                  via.name
                )
              : !canEdit
              ? gt('You must be a manager of this document')
              : !team.can_edit
              ? gt('Only a manager can add and remove from this team')
              : undefined,
          actionTooltip: attached
            ? gt(
                'Remove document from this team (this will also cancel any tasks related to the team.)'
              )
            : gt('Add document to this team'),
        };
      }}
      useDataHook={useTeams}
      paramsConverter={convertLocationParamsToFetchParams}
    />
  );
};
